// src/prints/HireBillPrint.jsx
import React, { useEffect, useState, useMemo } from "react";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import { useParams } from "react-router-dom";
import axios from "axios";
import Logo from "../assets/logo.png"; // fallback logo

const API_BASE =
  import.meta.env?.VITE_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  "https://rfs2.infraconsoft.net.in/api";

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const HireBillPrint = () => {
  useAuthRedirect();
  const { id, branch, vcode, billNo } = useParams();

  // --- Site / Branding state (company header) ---
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,      // absolute URL if available
    address: "",
    email: "",
    phone: "",
    _ts: 0,          // cache timestamp for cache-busting
  });

  // --- Bill page states ---
  const [details, setVehicleDetails] = useState({});
  const [data, setData] = useState([]); // table rows
  const [vehicle, setVehicle] = useState({});
  const [km, setKm] = useState("");
  const [lastDate, setLastDate] = useState(null);
  const [diesel1, setDiesel] = useState(null);
  const user = localStorage.getItem("username");

  // =========================
  // 1) Fetch SITE INFO (for header)
  // =========================
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/siteinfo`);
        const s = res?.data || {};
        // normalize + ensure absolute logo URL if server ever returns relative
        let logo = s.logo || null;
        if (logo && !/^https?:\/\//i.test(logo)) {
          if (!logo.startsWith("/")) logo = `/${logo}`;
          logo = `${API_ORIGIN}${logo}`;
        }
        if (!ignore) {
          setSite({
            ok: !!s.ok,
            name: s.name || "Infraconsoft",
            logo,
            address: s.address || "",
            email: s.email || "",
            phone: s.phone || "",
            _ts: Date.now(),
          });
        }
      } catch (e) {
        // keep defaults on failure
        if (!ignore) {
          setSite((prev) => ({ ...prev, _ts: Date.now() }));
        }
        console.error("Failed to load /siteinfo", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  // =========================
  // 2) Fetch diesel rate (latest)
  // =========================
  useEffect(() => {
    const fetchDiesel = async () => {
      try {
        const response = await axios.get(`${API_BASE}/vehicle_dieselrate_api`);
        const allVehicles = response.data.vehicles || [];
        const sorted = allVehicles
          .filter((item) => item.fld_date)
          .sort((a, b) => new Date(b.fld_date) - new Date(a.fld_date));
        const lastRate = sorted.length > 0 ? sorted[0].fld_rate : null;
        setDiesel(lastRate);
      } catch (error) {
        console.error("Error fetching last fld_rate:", error);
      }
    };
    fetchDiesel();
  }, []);

  // =========================
  // 3) Fetch bill details (top meta)
  // =========================
  useEffect(() => {
    const fetchVehicleDetails = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/bill_view_details?branch=${branch}&id=${id}`
        );

        if (response.data.billDetails && response.data.billDetails.length > 0) {
          const d = response.data.billDetails[0];
          setVehicleDetails({
            billNo: d.fld_billno,
            billDate: d.fld_billdate,
            party: d.fld_partyname,
            vehicleName: d.fld_vehiname,
            fld_branchid: d.fld_branchid,
            fld_vdate: d.fld_vdate,
            vcode: d.fld_equip,
            vehicleType: d.fld_vehitype,
            preparedBy: d.fld_billprepared,
            particular: d.fld_particular,
            hours: d.fld_hours,
            tot: d.fld_gamt,
            bata: d.fld_bata,
            bataTot: d.fld_batatot,
            totKm: d.fld_monthkm,
            breakAmt: d.fld_breakamt,
            breakTot: d.fld_batatot,
            matReason: d.fld_metdetection,
            tdsPer: d.fld_percent,
            tdsTot: d.fld_namt,
            breakHr: d.fld_breakhour,
            tdsAmt: d.fld_taxparti,
            mainReason: d.fld_metdetection,
            fld_totdeduction: d.fld_totdeduction,
            decReason: d.fld_decReason,
            mainAmt: d.fld_maintamt,
            otherAmt: d.fld_otheramt,
            balancePay: d.fld_balancepay,
            fld_metamt: d.fld_metamt,
          });
        }
      } catch (error) {
        console.error("Error fetching vehicle details:", error);
      }
    };
    fetchVehicleDetails();
  }, [branch, id]);

  // =========================
  // 4) Fetch vehicle basis & price
  // =========================
  useEffect(() => {
    const fetchVehicleDetails1 = async () => {
      try {
        const response = await axios.get(`${API_BASE}/vehicleBasis?vcode=${vcode}`);
        const v = response.data?.vBasis?.[0];
        if (v) {
          const fld_vbasis = v.fld_vbasis;
          const fld_vprice = v.fld_vprice;
          const fld_date = v.fld_date;
          const fld_vehappperiod = v.fld_vehappperiod;

          setVehicle({
            basis: fld_vbasis,        // Note: we store as 'basis'
            date: fld_date,
            fld_vprice: fld_vprice,
            fld_vehappperiod: fld_vehappperiod,
          });

          let Km = "";
          if (fld_vbasis === "Hour") Km = "Hour";
          else if (fld_vbasis === "Kmhour") Km = "Kmhour";
          else if (fld_vbasis === "Km") Km = "Km";
          else if (fld_vbasis === "Ton" || fld_vbasis === "Month") Km = "Km";

          setKm(Km);
        }
      } catch (error) {
        console.error("Error fetching vehicle basis:", error);
      }
    };
    if (vcode) fetchVehicleDetails1();
  }, [vcode]);

  // =========================
  // 5) Fetch bottom details (table rows)
  // =========================
  useEffect(() => {
    axios
      .get(`${API_BASE}/billReport_bottomDetails?billNo=${billNo}`)
      .then((response) => {
        const bottomDetails = response.data.bottomDetails || [];
        setData(bottomDetails);

        if (bottomDetails.length > 0) {
          const sorted = [...bottomDetails].sort(
            (a, b) => new Date(a.fld_vdate) - new Date(b.fld_vdate)
          );
          setLastDate(sorted[sorted.length - 1].fld_vdate);
        } else {
          setLastDate(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching bottom details:", error);
      });
  }, [billNo]);

  // =========================
  // 6) Totals used in “Deduction/Abstract” table
  // =========================
  const tdsAmt = parseFloat(details.tdsAmt) || 0;
  const mainAmt = parseFloat(details.mainAmt) || 0;
  const fld_totdeduction = parseFloat(details.fld_totdeduction) || 0;
  const fld_metamt = parseFloat(details.fld_metamt) || 0;

  const totals1 = {
    tdsAmt,
    mainAmt,
    fld_totdeduction,
    fld_metamt,
    totDed: tdsAmt + mainAmt + fld_totdeduction + fld_metamt,
  };

  // =========================
  // 7) Row aggregations (memoized)
  //  - Also returns diesal_cum (you referenced it later)
  // =========================
  const totals = useMemo(() => {
    let ff = 0;
    let totm = 0;
    let nodieseres = 0;
    let bs = 0;
    let a = 0; // unused outside but kept for parity
    let diesel2 = 0; // unused outside but kept for parity
    let totalAmount = 0;
    let diesal_cum = 0;
    let totalDieselRate = 0;

    data.forEach((item) => {
      const stkm = item.fld_stkm || 0;
      const endkm = item.fld_endkm || 0;
      ff += Math.round(endkm - stkm);

      const workingmins = item.fld_wohrs || 0;
      totm += workingmins;

      const noload = item.fld_noloads || 0;
      bs += noload;
      a = bs === 0 ? 1 : bs * 10;

      const diesel = item.fld_dieselqty || 0;
      nodieseres += diesel;

      const dieselRate = item.fld_dieselrate || 0;
      totalDieselRate += dieselRate;

      // protect against divide-by-null
      if (diesel1) {
        diesal_cum = ff / diesel1;
      }

      const totdiesel = diesel * (diesel1 || 1);
      diesel2 = workingmins ? totdiesel / workingmins : 0;

      const tons = item.fld_tons || 0;
      const price = vehicle.fld_vprice || 0;
      totalAmount += tons * price;
    });

    // Convert total mins to "HH.MM"
    const tothour = Math.floor(totm / 60);
    const min = totm % 60;
    const totalmin = Math.round(min * 1.666);
    const totalhours = `${tothour}.${totalmin.toString().padStart(2, "0")}`;

    return {
      ff,
      bs,
      nodieseres,
      totalAmount,
      totalDieselRate,
      totalhours,
      diesal_cum, // <— now returned
    };
  }, [data, vehicle, diesel1]);

  // =========================
  // 8) Helpers
  // =========================
  function convertNumberToWords(amount) {
    const words = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
      "Seventeen", "Eighteen", "Nineteen",
    ];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const numToWords = (num) => {
      if (num < 20) return words[num];
      if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? " " + words[num % 10] : "");
      if (num < 1000)
        return words[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " and " + numToWords(num % 100) : "");
      return "";
    };

    const getSegment = (n, label) => (n ? numToWords(n) + " " + label + " " : "");

    if (isNaN(amount)) return "Invalid Amount";
    const num = Math.floor(amount);
    if (num === 0) return "Zero";

    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const rest = num % 100;

    let result = "";
    result += getSegment(crore, "Crore");
    result += getSegment(lakh, "Lakh");
    result += getSegment(thousand, "Thousand");
    result += getSegment(hundred, "Hundred");
    if ((crore || lakh || thousand || hundred) && rest) result += "and ";
    result += numToWords(rest);

    return result.trim() + " Only";
  }

  const balance = parseFloat(details.balancePay) || 0;
  const balanceInWords = convertNumberToWords(balance);

  const handlePrint = () => window.print();

  // =========================
  // Render
  // =========================
  const logoSrc = site.logo ? `${site.logo}${site._ts ? `?v=${site._ts}` : ""}` : Logo;

  return (
    <div className="delivery-note-container">
      {/* Header Section */}
      <div className="header">
        <div className="logo">
          <img src={logoSrc} alt={`${site.name} Logo`} />
        </div>
        <div className="company-info">
          <h2 style={{ textTransform: "uppercase" }}>{site.name || "Infraconsoft"}</h2>
          <p>
            {site.address && <span>{site.address}.<br /></span>}
            {site.phone && <span>Ph: {site.phone}</span>}
            {(site.phone && site.email) && <span> | </span>}
            {site.email && (
              <span>
                E-mail: <strong>{site.email}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Delivery Details */}
      <div className="delivery-details">
        <div className="left">
          <p><strong>Bill No:</strong> {billNo}</p>
          <p><strong>Party Name:</strong> {details.party}</p>
          <p><strong>Location :</strong> {details.fld_branchid}</p>
          <p>
            <strong>Period:</strong>
            {vehicle.date && new Date(vehicle.date).toLocaleDateString("en-GB")} TO{" "}
            {lastDate && new Date(lastDate).toLocaleDateString("en-GB")}
          </p>
        </div>
        <div className="right">
          <p><strong>Equipment Code:</strong> {details.vcode}</p>
          <p><strong>Rate/Hour:</strong> {vehicle.fld_vprice}</p>
          <p><strong>Rate Approval Up to:</strong> {vehicle.fld_vehappperiod}</p>
        </div>
      </div>

      {/* Items Table */}
      <table className="items-table">
        <thead>
          <tr>
            <th className="dc-th">S.NO</th>
            <th className="dc-th">Date</th>
            <th className="dc-th">St {km}</th>
            <th className="dc-th">Cl {km}</th>
            <th className="dc-th">W. {km}</th>
            <th className="dc-th">CUM {km}</th>
            <th className="dc-th">HSD</th>
            <th className="dc-th">Trips</th>
            <th className="dc-th">Bill Amount</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            let ff = 0;
            let totm = 0;
            let tothour = 0;
            let nodieseres = 0;
            let bs = 0;
            let totalAmount = 0;
            let totalDieselRate = 0;

            const rows = data.map((item, index) => {
              const dateObj = item.fld_vdate ? new Date(item.fld_vdate) : null;
              const formattedDate = dateObj ? dateObj.toLocaleDateString("en-GB") : "";

              const stkm = item.fld_stkm || 0;
              const endkm = item.fld_endkm || 0;
              const wkm = Math.round(endkm - stkm);
              ff += wkm;

              const workingmins = item.fld_wohrs || 0;
              totm += workingmins;

              const noload = item.fld_noloads || 0;
              bs += noload;

              const diesel = item.fld_dieselqty || 0;
              nodieseres += diesel;

              const dieselRate = item.fld_dieselrate || 0;
              totalDieselRate += dieselRate;

              const tons = item.fld_tons || 0;
              const price = vehicle.fld_vprice || 0;
              const totalPrice = tons * price;
              totalAmount += totalPrice;

              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{formattedDate}</td>
                  <td>{stkm}</td>
                  <td>{endkm}</td>
                  <td>{wkm}</td>
                  <td>{ff}</td>
                  <td>{diesel}</td>
                  <td>{noload}</td>
                  <td>{totalPrice.toFixed(2)}</td>
                </tr>
              );
            });

            // Compute total hours (HH.MM) based on total minutes (when basis is Km or Hour)
            let totalhours = "";
            if (vehicle.basis === "Km" || vehicle.basis === "Hour") {
              tothour = Math.floor(totm / 60);
              const min = totm % 60;
              const totalmin = Math.round(min * 1.666); // Convert minutes to decimal
              totalhours = `${tothour}.${totalmin.toString().padStart(2, "0")}`;
            }

            return (
              <>
                {rows}
                <tr className="table-total">
                  <td colSpan={4}><strong>Total:</strong></td>
                  <td>{ff}</td>
                  <td></td>
                  <td></td>
                  <td>{bs}</td>
                  <td>{totalAmount.toFixed(2)}</td>
                </tr>
              </>
            );
          })()}
        </tbody>
      </table>

      <br /><br />

      {/* Summary / Abstract */}
      <table className="br-bill-summary table table-bordered text-center">
        <thead>
          <tr>
            <th colSpan="2">DESCRIPTION OF WORK</th>
            <th>AMOUNT IN RS</th>
            <th colSpan="2">ABSTRACT</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="2">TOTAL AMOUNT</td>
            <td></td>
            <td>WORK VOLUME</td>
            <td></td>
          </tr>
          <tr>
            <td rowSpan="5">Deduction:</td>
            <td>TDS @2.26%</td>
            <td>{details.tdsAmt}</td>
            <td>DIESEL COST</td>
            <td>{(totals.totalDieselRate || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td>MEALS EXPENSES</td>
            <td>{details.mainAmt}</td>
            <td>COST/CUM</td>
            <td>{totals.diesal_cum || 0}</td>
          </tr>
          <tr>
            <td>CASH ADVANCE</td>
            <td>{details.fld_totdeduction}</td>
            <td>DESCRIPTION</td>
            <td></td>
          </tr>
          <tr>
            <td>LUB/HSD</td>
            <td></td>
            <td>ACTUAL</td>
            <td>TARGET</td>
          </tr>
          <tr>
            <td>SPARES</td>
            <td>{details.fld_metamt}</td>
            <td>BILL AMOUNT</td>
            <td>TDS</td>
          </tr>
          <tr>
            <td colSpan="2">TOTAL DEDUCTION</td>
            <td>{totals1.totDed.toLocaleString("en-IN")}</td>
            <td>CU. BILL VALUE</td>
            <td></td>
          </tr>
          <tr>
            <td colSpan="2">NET PAYABLE:</td>
            <td>{details.balancePay}</td>
            <td>PAID SO FOR RS:</td>
            <td></td>
          </tr>
          <tr>
            <td colSpan="2">CHEQUE IN FAVOUR OF:</td>
            <td colSpan="3">BALANCE UP TO</td>
          </tr>
          <tr>
            <td colSpan="5" className="text-start br-payment-words">
              <p>PAYMENT DETAILS: <strong>₹ {balance.toLocaleString("en-IN")}</strong></p>
              <p className="text-muted">In words: <em>{balanceInWords}</em></p>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="5" className="text-center">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  textAlign: "center",
                  width: "100%",
                  gap: "10px",
                }}
              >
                <div style={{ flex: 1 }}><strong>PREPARED BY ({user})</strong></div>
                <div style={{ flex: 1 }}><strong>CHECKED BY</strong></div>
                <div style={{ flex: 1 }}><strong>SITE INCHARGE</strong></div>
                <div style={{ flex: 1 }}><strong>APPROVED BY</strong></div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>

      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={handlePrint}
          style={{
            backgroundColor: "#4a90e2",
            color: "white",
            border: "none",
            padding: "12px 24px",
            fontSize: "16px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background 0.3s ease-in-out",
            marginTop: "20px",
          }}
        >
          Print
        </button>
      </div>
    </div>
  );
};

export default HireBillPrint;
